import source from "./subscriber-usage-policy.md?raw";

/**
 * The AnkhOS subscriber Usage Policy template as plain text, for an operator to
 * adapt into its own Terms of Service.
 *
 * The source is io.ankhos/legal-subscriber-template/USAGE-POLICY.md, copied
 * verbatim beside this file (approved by the platform owner 2026-09-07, legal
 * review pending). The portal renders documents as plain text, so the Markdown
 * is flattened here: headings and emphasis lose their marks, a table becomes
 * one line per row, and the notes addressed to operators (⚠️ paragraphs and
 * "*Operators: ...*") are dropped -- they are instructions, not terms. Every
 * «placeholder» is kept, so the editor can insist they are replaced.
 */
export function plainTemplate(markdown: string): string {
  const body = markdown.replace(/\r\n?/g, "\n").replace(/^---\n[\s\S]*?\n---\n/, "");
  const paragraphs = body.split(/\n\s*\n/);
  const out: string[] = [];
  for (const para of paragraphs) {
    const text = para.trim();
    if (!text) continue;
    if (text.startsWith("⚠️") || /^\*Operators:/.test(text)) continue;
    const lines = text
      .split("\n")
      .filter((l) => !/^\|[\s|:-]+\|$/.test(l.trim()))
      .map((l) => {
        const t = l.trim();
        if (t.startsWith("|")) {
          return t
            .replace(/^\||\|$/g, "")
            .split("|")
            .map((c) => c.trim())
            .join(" · ");
        }
        return l;
      })
      .map((l) => l.replace(/^#{1,6}\s+/, "").replace(/🔑\s*/g, ""));
    // A wrapped Markdown paragraph is one paragraph: join its lines, except in
    // lists, where each item keeps its own line.
    const list = lines.every((l, i) => i === 0 || /^\s*(\d+\.|-)\s/.test(l) || /^\s{2,}\S/.test(l));
    const joined = list && lines.length > 1 ? lines.map((l) => l.replace(/^\s{2,}(?=\S)/, "   ")).join("\n") : lines.map((l) => l.trim()).join(" ");
    // Emphasis last: a bold phrase may wrap across source lines.
    out.push(
      joined
        .replace(/\*\*([\s\S]+?)\*\*/g, "$1")
        .replace(/(^|[^*\w])\*(?!\s)([^*]+?)\*(?!\w)/g, "$1$2"),
    );
  }
  return out.join("\n\n");
}

export const SUBSCRIBER_TERMS_TEMPLATE = plainTemplate(source);
